import React, { useState } from 'react';
import { Box, Typography, Chip, Card, CardContent, Grid, Button } from '@mui/material';
import { useRecordContext } from 'react-admin';

const OculusSubscriptionsTab = () => {
  const record = useRecordContext();
  const [filter, setFilter] = useState('all');

  if (!record?.oculus?.length) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
          🎮 Oculus Subscriptions
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No hay suscripciones de Oculus disponibles para este usuario.
        </Typography>
      </Box>
    );
  }

  const getStatusColor = (active, trial, cancelled) => {
    if (cancelled) {
      return { color: '#d32f2f', backgroundColor: '#f8d7da', borderColor: '#f5c6cb' };
    }
    return trial
      ? { color: '#f57c00', backgroundColor: '#fff3cd', borderColor: '#ffecb5' }
      : active
      ? { color: '#388e3c', backgroundColor: '#d4edda', borderColor: '#c3e6cb' }
      : { color: '#d32f2f', backgroundColor: '#f8d7da', borderColor: '#f5c6cb' };
  };

  const formatPrice = (price, currency) =>
    !price || price === 0 ? 'Gratis' : `${currency} ${parseFloat(price).toFixed(2)}`;

  const filtered = record.oculus.filter(sub => {
    if (filter === 'all') return true;
    if (filter === 'active') return sub.is_active;
    if (filter === 'inactive') return !sub.is_active;
    if (filter === 'trial') return sub.is_trial;
    return true;
  });

  const stats = {
    total: record.oculus.length,
    active: record.oculus.filter(s => s.is_active).length,
    inactive: record.oculus.filter(s => !s.is_active).length,
    trial: record.oculus.filter(s => s.is_trial).length,
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* ... contenido previo ... */}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
              🔍 No se encontraron suscripciones
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No hay suscripciones que coincidan con el filtro seleccionado: <strong>{filter}</strong>
            </Typography>
            <Button variant="outlined" onClick={() => setFilter('all')} sx={{ mt: 2 }}>
              Ver todas las suscripciones
            </Button>
          </Box>
        ) : (
          filtered.map((sub, i) => {
            const isCancelled = sub.cancellation_time;
            const statusColor = getStatusColor(sub.is_active, sub.is_trial, isCancelled);
            return (
              <Card
                key={sub.subscription_id || i}
                sx={{
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 8px rgba(0,0,0,0.12)' },
                }}
              >
                <CardContent>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        ID de Suscripción
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {sub.subscription_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        SKU
                      </Typography>
                      <Typography variant="body1">{sub.sku || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Estado
                      </Typography>
                      <Chip
                        label={isCancelled ? 'Cancelada' : sub.is_trial ? 'Trial' : sub.is_active ? 'Activa' : 'Inactiva'}
                        sx={{ ...statusColor, fontWeight: 'bold', fontSize: '0.875rem', width: 'fit-content' }}
                      />
                      {sub.cancellation_time && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                          Cancelada: {new Date(sub.cancellation_time).toLocaleDateString()}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Periodo Actual
                      </Typography>
                      <Typography variant="body2">
                        {sub.period_start_time ? new Date(sub.period_start_time).toLocaleDateString() : 'N/A'} -{' '}
                        {sub.period_end_time ? new Date(sub.period_end_time).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Nuevo Grid para owner_id, is_active y trial_type */}
                  <Grid container spacing={3} alignItems="center" sx={{ mt: 2 }}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Owner ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {sub.owner_id || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Activa
                      </Typography>
                      <Typography variant="body1">
                        {isCancelled ? 'Cancelada' : sub.is_active ? 'Sí' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Tipo de Trial
                      </Typography>
                      <Typography variant="body1">
                        {sub.trial_type || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Fecha de Cancelación
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {sub.cancellation_time ? new Date(sub.cancellation_time).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Última Actualización
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {sub.updated_at ? new Date(sub.updated_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Grid>
                
                  </Grid>

                 
              </CardContent>

              

              <Box sx={{ mb: 4, p: 3, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  💰 Resumen de Precios
                </Typography>
                <Grid container spacing={2}>
                  {filtered.map((sub) => (
                    <Grid item xs={12} sm={6} md={4} key={sub.subscription_id}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        
                        <Typography variant="body2">
                          Precio Actual: <strong>{formatPrice(sub.current_price, sub.current_currency)}</strong>
                        </Typography>
                        <Typography variant="body2">
                          Próximo Precio: <em>{formatPrice(sub.next_price, sub.next_currency)}</em>
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Próxima Renovación: {sub.cancellation_time ? '' : sub.next_renewal_time ? new Date(sub.next_renewal_time).toLocaleDateString() : 'N/A'}

                      </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
                
              </Card>

              
            );
          })
        )}
      </Box> 

    </Box>
  );
};

export default OculusSubscriptionsTab;